import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useTheme, useMediaQuery } from '@mui/material';
import { useOptionsService } from '#service/autocomplete-service/hooks';
import { useDebounce } from '#component/general/list-filter/hooks';
import {
  Container,
  SelectWrapper,
  SelectTrigger,
  TriggerContent,
  SelectedCount,
  CountBadge,
  ArrowIcon,
  Dropdown,
  SearchContainer,
  SearchInputWrapper,
  StyledSearchIcon,
  SearchInput,
  ClearButton,
  ClearIcon,
  LoadingText,
  OptionsList,
  OptionItem,
  OptionLabel,
  NoOptions,
  TableCheckbox
} from './styles';

export const SelectWithCounter = ({
  name,
  relatedTo,
  onChange,
  label,
  searchPlaceholder = 'Search...',
  noOptionsText = 'No options found',
  disabled,
  getOptionLabel,
  getOptionValue,
  searchFields,
  combinedWith,
  fetchOptionsService,
  optionServiceConfig
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const selected = useSelector(s => s.listFilter?.[relatedTo]?.[name]?.value, shallowEqual);
  const [value, setValue] = useState(selected || []);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [alignRight, setAlignRight] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const listboxIdRef = useRef(`select-listbox-${Math.random().toString(36).substr(2, 9)}`);
  const listboxId = listboxIdRef.current;
  const safeValue = useMemo(() => value || [], [value]);

  const { options, status, ...serviceMethods } =
  useOptionsService(searchFields, fetchOptionsService, { combinedWith, ...optionServiceConfig });

  useEffect(() => {
    setValue(selected ?? []);
  }, [selected]);

  useEffect(() => {
    if (!isOpen) {
      setAlignRight(false);
      return;
    }

    const checkPosition = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const dropdownWidth = isMobile ? 280 : 360;

        const wouldOverflowRight = containerRect.left + dropdownWidth > viewportWidth;

        setAlignRight(wouldOverflowRight);
      }
    };

    requestAnimationFrame(checkPosition);
  }, [isOpen, isMobile]);

  const loading = status === 'loading';

  const handleOnOpen = useCallback(() => {
    serviceMethods.fetchOptions();
  }, [serviceMethods]);

  const handleClose = useCallback(() => {
    serviceMethods.clearOptions();
  }, [serviceMethods]);

  const getLabelFn = useCallback(
    option => (getOptionLabel ? getOptionLabel(option) : option.label),
    [getOptionLabel]
  );

  const getValueFn = useCallback(
    option => (getOptionValue ? getOptionValue(option) : option.data?.id ?? option.label),
    [getOptionValue]
  );

  const selectedValues = useMemo(() => new Set(safeValue.map(getValueFn)), [safeValue, getValueFn]);

  const safeOptions = useMemo(() => options || [], [options]);

  const debouncedFetchOptions = useDebounce(query => {
    if (query.length >= 2) {
      serviceMethods.fetchOptions(query);
    } else if (query.length === 0) {
      serviceMethods.fetchOptions();
    }
  }, 300);

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (disabled) return;
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen) {
      handleOnOpen();
    } else {
      setSearchQuery('');
      handleClose();
    }
  };

  const handleOptionToggle = option => {
    const optionValue = getValueFn(option);
    const isSelected = selectedValues.has(optionValue);

    let newValue;
    if (isSelected) {
      newValue = safeValue.filter(v => getValueFn(v) !== optionValue);
    } else {
      newValue = [...safeValue, option];
    }

    setValue(newValue);
    onChange(newValue);
  };

  const handleSearchChange = event => {
    const newValue = event.target.value;
    setSearchQuery(newValue);

    if (!newValue.trim().length) {
      debouncedFetchOptions.cancel();
      serviceMethods.fetchOptions();
      return;
    }

    debouncedFetchOptions(newValue.trim());
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    debouncedFetchOptions.cancel();
    serviceMethods.fetchOptions();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      debouncedFetchOptions.cancel();
      handleClose();
    }
  };

  const isOptionSelected = option => selectedValues.has(getValueFn(option));

  return (
    <Container ref={containerRef}>
      <SelectWrapper>
        <SelectTrigger
          onClick={handleToggleDropdown}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        >
          <TriggerContent>
            <div>{label}</div>
            {safeValue.length > 0 && (
              <SelectedCount>
                <CountBadge>{safeValue.length}</CountBadge>
              </SelectedCount>
            )}
          </TriggerContent>
          <ArrowIcon isOpen={isOpen} />
        </SelectTrigger>

        {isOpen && (
          <Dropdown ref={dropdownRef} role="listbox" id={listboxId} alignRight={alignRight}>
            <SearchContainer>
              <SearchInputWrapper>
                <StyledSearchIcon />
                <SearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
                {searchQuery && (
                  <ClearButton
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </ClearButton>
                )}
              </SearchInputWrapper>
            </SearchContainer>

            <OptionsList>
              {loading ? (
                <LoadingText>Loading...</LoadingText>
              ) : safeOptions.length === 0 ? (
                <NoOptions>{searchQuery.length > 0 && searchQuery.length < 2 ? 'Enter at least 2 characters' : noOptionsText}</NoOptions>
              ) : (
                safeOptions.map(option => {
                  const optionValue = getValueFn(option);
                  const optionLabel = getLabelFn(option);
                  const isSelected = isOptionSelected(option);

                  const handleKeyDownOption = event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleOptionToggle(option);
                    }
                  };

                  return (
                    <OptionItem
                      key={optionValue}
                      isSelected={isSelected}
                      onClick={() => handleOptionToggle(option)}
                      onKeyDown={handleKeyDownOption}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={0}
                    >
                      <TableCheckbox
                        color="primary"
                        checked={isSelected}
                        tabIndex={-1}
                        inputProps={{
                          'aria-label': `select ${optionLabel}`
                        }}
                      />
                      <OptionLabel title={optionLabel}>
                        {optionLabel}
                      </OptionLabel>
                    </OptionItem>
                  );
                })
              )}
            </OptionsList>
          </Dropdown>
        )}
      </SelectWrapper>

    </Container>
  );
};
